import React, { FC } from "react"
import { Route, Routes } from "react-router-dom"

import { useTranslation } from "react-i18next"

import { Admins } from "views/Admins"
import { BalancePage } from "views/Balance"
import { BankTransfers } from "views/BankTransfers"
import { BetbyBonusesPage } from "views/BetbyBonuses"
import { Bonus2Admin } from "views/Bonus2Admin"
import { Bonueses } from "views/Bonuses"
import { CMSPage } from "views/CMSPage"
import { CmsSeo } from "views/CmsSeo"
import { GameManagement } from "views/GameManagement"
import { GGRMonthlyReport } from "views/GGRMonthlyReport"
import { KYCFiles } from "views/KYCFiles"
import { PromotionCreate } from "views/PromotionCreate"
import { PromotionEdit } from "views/PromotionEdit"
import { Promotions } from "views/Promotions"
import { RoleManagement } from "views/RoleManagement"
import { S2SPostbacks } from "views/S2SPostbacks"
import { Settings } from "views/Settings"
import { TagManagementPage } from "views/TagManagement"
import { TokenAdmin } from "views/TokenAdmin"
import { TournamentCreate } from "views/TournamentCreate"
import { TournamentDetail } from "views/TournamentDetail"
import { TournamentEdit } from "views/TournamentEdit"
import { TournamentMonitor } from "views/TournamentMonitor"
import { Tournaments } from "views/Tournaments"

import { ProtectedRoute } from "router/ProtectedRoute"

import { PERMISSIONS } from "constants/permissions"

import { NavBar } from "./components/navigation/NavBar"
import { URL } from "./constants/api/requests"
import { PermissionsProvider } from "./contexts/PermissionsContext"
import { useProps } from "./contexts/PropsContext"
import { AuthenticatedRoutes } from "./router/AuthenticatedRoutes"
import { Activity } from "./views/Activity"
import { Challenges } from "./views/Challenges"
import { Dashboard } from "./views/Dashboard"
import { Home } from "./views/Home"
import { PromoCodes } from "./views/PromoCodes"
import { Referrals } from "./views/Referrals"
import { RiskDashboard } from "./views/RiskDashboard"
import { Transactions } from "./views/Transactions"
import { User } from "./views/User"
import { Users } from "./views/Users"

export const App: FC = () => {
  const { darkMode } = useProps()
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <PermissionsProvider>
      <div className={darkMode ? "dark" : ""}>
        <div className="w-full h-screen min-h-screen overflow-auto bg-white dark:bg-grey-dark">
          <NavBar>
            <Routes>
              <Route path={URL.HOME} element={<Home />} />
              <Route element={<AuthenticatedRoutes />}>
                <Route
                  path={URL.DASHBOARD}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_DASHBOARD}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.CMS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_CMS}>
                      <CMSPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.USERS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_USERS}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.USER}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_USER_PROFILE}>
                      <User />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.BONUSES}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_BONUSES}>
                      <Bonueses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.BONUS2_ADMIN}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_BONUS2}>
                      <Bonus2Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TOKEN_ADMIN}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TOKEN}>
                      <TokenAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TRANSACTIONS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TRANSACTIONS}>
                      <Transactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.BANK_TRANSFER}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_BANK_TRANSFERS}>
                      <BankTransfers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.PROMO_CODES}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_PROMO_CODES}>
                      <PromoCodes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.ACTIVITY}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_ACTIVITY}>
                      <Activity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.KYC_FILES}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_KYC}>
                      <KYCFiles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.REFERRALS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_REFERRALS}>
                      <Referrals />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.SETTINGS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_SETTINGS}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.ADMINS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_ADMINS}>
                      <Admins />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.ROLE_MANAGEMENT}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.ROLE_MANAGEMENT_EDIT}>
                      <RoleManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.GAME_MANAGEMENT}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_GAME_MANAGEMENT}>
                      <GameManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.CMS_SEO}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_CMS}>
                      <CmsSeo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.S2S_POSTBACKS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_POSTBACKS}>
                      <S2SPostbacks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.GGR_MONTHLY_REPORT}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_GGR_REPORT}>
                      <GGRMonthlyReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.PROMOTIONS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_PROMOTIONS}>
                      <Promotions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.PROMOTIONS_CREATE}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_PROMOTIONS}>
                      <PromotionCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.PROMOTION}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_PROMOTIONS}>
                      <PromotionEdit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TAGS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TAGS}>
                      <TagManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.BALANCE}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_BALANCE}>
                      <BalancePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TOURNAMENTS}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TOURNAMENTS}>
                      <Tournaments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TOURNAMENTS_CREATE}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TOURNAMENTS}>
                      <TournamentCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TOURNAMENT_EDIT}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TOURNAMENTS}>
                      <TournamentEdit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TOURNAMENT}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TOURNAMENTS}>
                      <TournamentDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.TOURNAMENT_MONITOR}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_TOURNAMENTS}>
                      <TournamentMonitor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.BETBY_BONUSES}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_BETBY_BONUSES}>
                      <BetbyBonusesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.RISK_DASHBOARD}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_RISK}>
                      <RiskDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={URL.CHALLENGES}
                  element={
                    <ProtectedRoute permission={PERMISSIONS.PAGE_CHALLENGES}>
                      <Challenges />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path={URL.NOT_FOUND} element={<div>{t("common.notFound")}</div>} />
            </Routes>
          </NavBar>
        </div>
      </div>
    </PermissionsProvider>
  )
}
